import React from 'react'
import moment from '../../lib/moment'

import {
  VictoryChart,
  VictoryAxis,
  VictoryLine,
  VictoryLabel,
  VictoryScatter,
  Line,
} from 'victory'

import styles from './TideGraphStyles'


const timeFormat = 'h:mm a'


export default ({now, date, tides, tideHeightRange}) => {

  const { min, max } = tideHeightRange
  const minMaxDiff   = max - min

  if (tides.length === 0)
    return null

  const startOfDay = date.clone().startOf('day')
  const endOfDay   = date.clone().add(1, 'day').startOf('day').seconds(1)

  const xDomain = [startOfDay, endOfDay]
  const yDomain = [tideHeightRange.min, tideHeightRange.max]
  const domain  = {x: xDomain, y: yDomain}
  // const tickValues = [
  //   // date.clone().hour(0).minutes(0).seconds(0),
  //   date.clone().hour(6).minutes(0).seconds(0),
  //   date.clone().hour(12).minutes(0).seconds(0),
  //   date.clone().hour(18).minutes(0).seconds(0),
  //   // date.clone().hour(24).minutes(0).seconds(0),
  // ]

  const xDomainMoment = moment.range(...xDomain)
  const todayTides = tides.filter(t => t.moment.within(xDomainMoment))
  const highTides = todayTides.filter(t => t.type === 'High')
  const lowTides = todayTides.filter(t => t.type === 'Low')

  const fakeMHHW = max - minMaxDiff * 0.15
  const MHHWdata = [
    {x: startOfDay, y: fakeMHHW},
    {x: endOfDay,   y: fakeMHHW},
  ]
  const MLLWdata = [
    {x: startOfDay, y: 0},
    {x: endOfDay,   y: 0},
  ]


  return (
    <div>

      {/* Gradient Definition */}
      <div style={styles.gradientDefinition}>
        <svg>
          <defs>
            <linearGradient id="tideLineGradient"
              x1="0%" y1="0%" x2="0%" y2="100%"
            >
              <stop offset="0%"   stopColor="#84CCD6"/>
              <stop offset="100%" stopColor="#CCAA7A"/>
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Chart */}
      <VictoryChart
        height={240}
        width={400}
        domain={domain}
        domainPadding={{y: [10, 10]}}
        padding={{bottom: 0, top: 10}}
        style={styles.chart}
      >

        {/* Height axis */}
        <VictoryAxis
          dependentAxis
          crossAxis={false}
          style={styles.heightAxis}
          orientation="right"
          tickFormat={t => `${t} ft.`}
          tickLabelComponent={<VictoryLabel dy={12} dx={-45} />}
          axisComponent={<Line type={"axis"} offsetX={0}/>}
        />

        {/* Mean water lines */}
        <VictoryLine
          style={styles.meanHighWater}
          data={MHHWdata}
        />
        <VictoryLine
          style={styles.meanLowWater}
          data={MLLWdata}
        />

        {/* Tide label white backing */}
        <VictoryScatter
          data={todayTides}
          x="moment"
          y="v"
          size={8}
          labels={d => `${d.moment.format(timeFormat)}\n${d.v.toFixed(1)} ft.`}
          labelComponent={<TideLabelBacking />}
        />

        {/* Tide line */}
        <VictoryLine
          style={styles.tideLineBorder}
          data={tides}
          x="moment"
          y="v"
          interpolation="monotoneX"
        />
        <VictoryLine
          style={styles.tideLine}
          data={tides}
          x="moment"
          y="v"
          interpolation="monotoneX"
        />

        {/* Tide dots */}
        <VictoryScatter
          style={styles.highTideDot}
          data={highTides}
          x="moment"
          y="v"
          size={8}
        />
        <VictoryScatter
          style={styles.lowTideDot}
          data={lowTides}
          x="moment"
          y="v"
          size={8}
        />

        {/* Tide labels */}
        <VictoryScatter
          style={styles.tideLabel}
          data={todayTides}
          x="moment"
          y="v"
          size={7}
          labels={d => `${d.moment.format(timeFormat)}\n${d.v.toFixed(1)} ft.`}
          labelComponent={<TideLabel />}
        />

        {/* Now line */}
        <NowLine
          now={now}
          date={date}
          min={min}
          max={max}
        />

      </VictoryChart>
    </div>
  )
}

function TideLabelBacking(props) {
  const width  = 60
  const height = 40
  const x = props.x - width/2
  const y = props.y - height/2 + tideLabelDy(props.datum)

  return (
    <rect
      style={styles.tideLabelBacking}
      width={width}
      height={height}
      x={x}
      y={y}
    />
  )
}

function TideLabel(props) {
  return (
    <VictoryLabel
      {...props}
      verticalAnchor='middle'
      dy={tideLabelDy}
    />
  )
}

function tideLabelDy(d) {
  if (d.type==='High')
    return  40
  else if (d.v > 0)
    return -20
  else
    return -40
}

function NowLine(props) {
  const {
    now,
    date,
    min,
    max,
  } = props

  // Don't render nowLine if the selected date is not today
  if (!date.isSame(now, 'day'))
    return null

  const nowLineData = [
    {x: now, y: min},
    {x: now, y: max},
  ]

  const labelDatum = {
    x: now,
    y: max,
  }

  const nowLabelProps = {
    ...props,
    datum: labelDatum,
    textAnchor: 'middle',
    text: now.format(timeFormat),
  }

  const rectWidth  = 68
  const rectHeight = 26

  const rectProps = {
    x: props.scale.x(labelDatum.x) - rectWidth/2,
    y: props.scale.y(labelDatum.y) - rectHeight/2,
    width: rectWidth,
    height: rectHeight,
  }

  return (
    <g>
      <VictoryLine
        {...props}
        data={nowLineData}
        style={styles.nowLineBacking}
      />
      <VictoryLine
        {...props}
        data={nowLineData}
        style={styles.nowLine}
      />
      <rect
        {...rectProps}
        style={styles.nowLabelBacking}
      />
      <VictoryLabel
        {...nowLabelProps}
        style={styles.nowLabel}
      />
    </g>
  )
}



// WEBPACK FOOTER //
// ./src/components/DayPage/TideGraph.js